import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Timestamp,
  collection,
  doc,
  onSnapshot,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/core/AuthContext';
import { db, functions } from '@/core/firebase';
import { TrainerCard } from '../components/dashboard/TrainerCard';
import { WeeklyProgress } from '../components/dashboard/WeeklyProgress';
import { UpcomingWorkouts } from '../components/dashboard/UpcomingWorkouts';
import { WorkoutHistory } from '../components/dashboard/WorkoutHistory';
import { QuickActions } from '../components/dashboard/QuickActions';
import { Button, Card } from '../components/ui';
import { ALERTS } from '../styles/classNames';
import { BookingModal } from '../components/scheduling/BookingModal';
import { RescheduleModal } from '../components/scheduling/RescheduleModal';
import { Paywall } from '../components/Paywall';
import { WeeklyCalendar } from '../components/scheduling/WeeklyCalendar';
import { TrainerContactSheet } from '../components/trainer/TrainerContactSheet';
import { formatDateTimeForNotification } from '@/shared/utils/dates';
import type { TimeSlot } from '../components/scheduling/AvailabilityPicker';
import type {
  AssignmentStatus,
  WorkoutAssignment,
  WorkoutSession,
  WorkoutTemplate,
} from '../types';
import { useIsMobile } from '../lib/useIsMobile';
import { useTrainerDetails } from '@/lib/useTrainerDetails';

import { SEOHead } from '../components/SEOHead';
import { usePremiumAccess } from '../lib/usePremiumAccess';
import {
  cancelSessionNotifications,
  scheduleWorkoutReminder,
} from '@/core/notifications';
import { startAdHocWorkoutSession } from '@/lib/startAdHocWorkoutSession';

const DASHBOARD_VISIBLE_ASSIGNMENT_STATUSES: AssignmentStatus[] = [
  'pending',
  'reminded',
  'confirmed',
];

const getAssignmentWorkoutName = (
  assignment: WorkoutAssignment,
  fallback = 'Workout'
) =>
  assignment.workoutTemplateName ||
  fallback;

interface StartWorkoutSessionResponse {
  success: boolean;
  sessionId: string;
  status: 'in_progress';
  alreadyStarted?: boolean;
}

export function ClientDashboard() {
  const { user, userData, loading, refreshUserData } = useAuth();
  const [assignments, setAssignments] = useState<WorkoutAssignment[]>([]);
  const [sessions, setSessions] = useState<WorkoutSession[]>([]);
  const [startingId, setStartingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assignmentsLoadError, setAssignmentsLoadError] = useState<string | null>(null);
  const [sessionsLoadError, setSessionsLoadError] = useState<string | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showContactSheet, setShowContactSheet] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [rescheduleAssignment, setRescheduleAssignment] = useState<WorkoutAssignment | null>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isPremiumActive, refresh, shouldRequirePremium } = usePremiumAccess();

  const showIntroPrompt = useMemo(() => {
    return (
      userData?.onboardingComplete === true &&
      userData?.introSessionBooked !== true &&
      assignments.length === 0
    );
  }, [userData, assignments]);

  const trainerId = useMemo(
    () => userData?.trainerId || userData?.trainerProfileId,
    [userData]
  );
  const templateNames = useMemo(() => new Map<string, string>(), []);

  const aiTrainerId = useMemo(
    () => userData?.aiTrainerId,
    [userData]
  );
  const trainerDetails = useTrainerDetails({
    trainerId,
    aiTrainerId,
    enabled: !loading && Boolean(userData),
  });

  useEffect(() => {
    if (loading || !user) return;

    const assignmentsQuery = query(
      collection(db, 'workoutAssignments'),
      where('clientId', '==', user.uid)
    );

    const unsubAssignments = onSnapshot(
      assignmentsQuery,
      (snapshot) => {
        setAssignmentsLoadError(null);
        const docs = snapshot.docs.map(
          (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WorkoutAssignment)
        );
        console.log('[ClientDashboard] Assignments updated:', docs.length, docs);
        setAssignments(docs);
      },
      (listenerError) => {
        console.error('[ClientDashboard] ❌ ERROR in workoutAssignments listener:', listenerError.code, listenerError.message);
        setAssignmentsLoadError('Unable to load your workout assignments right now.');
      }
    );

    const sessionsQuery = query(
      collection(db, 'workoutSessions'),
      where('clientId', '==', user.uid)
    );

    const unsubSessions = onSnapshot(
      sessionsQuery,
      (snapshot) => {
        setSessionsLoadError(null);
        const docs = snapshot.docs.map(
          (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WorkoutSession)
        );
        setSessions(docs);
      },
      (listenerError) => {
        console.error('[ClientDashboard] ❌ ERROR in workoutSessions listener:', listenerError.code, listenerError.message);
        setSessionsLoadError('Unable to load your session history right now.');
      }
    );

    return () => {
      unsubAssignments();
      unsubSessions();
    };
  }, [user, loading]);

  useEffect(() => {
    if (loading || !user || !userData?.availabilitySlots?.length) return;

    const ensureUpcomingSessions = httpsCallable(functions, 'ensureUpcomingSessions');
    ensureUpcomingSessions({})
      .then((result: any) => {
        if (result.data?.created > 0) {
          console.log(`[ClientDashboard] Created ${result.data.created} new session(s)`);
        }
      })
      .catch((err) => {
        console.warn('[ClientDashboard] Failed to ensure upcoming sessions:', err);
      });
  }, [user, loading, userData?.availabilitySlots]);

  const rotationTemplateIds = userData?.trainingPlan?.rotationTemplates || [];
  const dailyRotationTemplateIds = userData?.trainingPlan?.dailyRotationTemplateIds || [];
  const lastCompletedDailyTemplateId = userData?.trainingPlan?.lastCompletedDailyTemplateId;
  const [rotationTemplates, setRotationTemplates] = useState<WorkoutTemplate[]>([]);
  const [dailyRotationTemplates, setDailyRotationTemplates] = useState<WorkoutTemplate[]>([]);

  useEffect(() => {
    if (rotationTemplateIds.length === 0) {
      setRotationTemplates([]);
      return;
    }

    const unsubscribers = rotationTemplateIds.map((templateId) =>
      onSnapshot(doc(db, 'workoutTemplates', templateId), (snap) => {
        setRotationTemplates((prev) => {
          const next = prev.filter((template) => template.id !== templateId);
          if (!snap.exists()) {
            return rotationTemplateIds
              .map((id) => next.find((template) => template.id === id))
              .filter(Boolean) as WorkoutTemplate[];
          }

          const updated = [
            ...next,
            { id: snap.id, ...snap.data() } as WorkoutTemplate,
          ];

          return rotationTemplateIds
            .map((id) => updated.find((template) => template.id === id))
            .filter(Boolean) as WorkoutTemplate[];
        });
      })
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [rotationTemplateIds]);

  useEffect(() => {
    if (dailyRotationTemplateIds.length === 0) {
      setDailyRotationTemplates([]);
      return;
    }

    const unsubscribers = dailyRotationTemplateIds.map((templateId) =>
      onSnapshot(doc(db, 'workoutTemplates', templateId), (snap) => {
        setDailyRotationTemplates((prev) => {
          const next = prev.filter((template) => template.id !== templateId);
          if (!snap.exists()) {
            return dailyRotationTemplateIds
              .map((id) => next.find((template) => template.id === id))
              .filter(Boolean) as WorkoutTemplate[];
          }

          const updated = [
            ...next,
            { id: snap.id, ...snap.data() } as WorkoutTemplate,
          ];

          return dailyRotationTemplateIds
            .map((id) => updated.find((template) => template.id === id))
            .filter(Boolean) as WorkoutTemplate[];
        });
      })
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [dailyRotationTemplateIds]);

  const rotationInProgressSessions = useMemo(
    () =>
      sessions.filter(
        (session) =>
          session.status === 'in_progress' &&
          rotationTemplateIds.includes(session.workoutTemplateId)
      ),
    [sessions, rotationTemplateIds]
  );

  const dailyTemplate = useMemo(() => {
    if (dailyRotationTemplates.length === 0) return null;
    if (!lastCompletedDailyTemplateId) return dailyRotationTemplates[0] || null;
    const lastIndex = dailyRotationTemplateIds.indexOf(lastCompletedDailyTemplateId);
    if (lastIndex === -1) return dailyRotationTemplates[0] || null;
    const nextIndex = (lastIndex + 1) % dailyRotationTemplateIds.length;
    return dailyRotationTemplates[nextIndex] || dailyRotationTemplates[0] || null;
  }, [dailyRotationTemplateIds, dailyRotationTemplates, lastCompletedDailyTemplateId]);

  const dailyInProgressSession = useMemo(
    () =>
      dailyTemplate
        ? sessions.find(
            (session) =>
              session.status === 'in_progress' &&
              session.workoutTemplateId === dailyTemplate.id
          ) || null
        : null,
    [sessions, dailyTemplate]
  );

  useEffect(() => {
    assignments.forEach((assignment) => {
      if (assignment.status !== 'pending') {
        void cancelSessionNotifications(assignment.id);
        return;
      }

      const scheduledFor = (assignment.scheduledFor as Timestamp).toDate();
      if (scheduledFor.getTime() <= Date.now()) {
        void cancelSessionNotifications(assignment.id);
        return;
      }

      void scheduleWorkoutReminder(
        scheduledFor,
        assignment.id,
        getAssignmentWorkoutName(assignment)
      );
    });
  }, [assignments]);

  const startSession = async (assignment: WorkoutAssignment) => {
    if (!user) {
      setError('Sign in to start your workout.');
      return;
    }

    setStartingId(assignment.id);
    setError(null);

    try {
      const startWorkoutSessionFn = httpsCallable<
        { assignmentId: string },
        StartWorkoutSessionResponse
      >(functions, 'startWorkoutSession');
      const result = await startWorkoutSessionFn({ assignmentId: assignment.id });
      const sessionId = result.data.sessionId;

      await cancelSessionNotifications(assignment.id);

      navigate(`/session/${sessionId}`);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Unable to start session');
    } finally {
      setStartingId(null);
    }
  };

  const workoutStreak = useMemo(() => {
    const completedSessions = sessions.filter(s => s.status === 'completed' && s.completedAt);
    if (completedSessions.length === 0) return 0;

    const completedDates = new Set<string>();
    completedSessions.forEach(session => {
      const date = (session.completedAt as Timestamp).toDate();
      const dateStr = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      completedDates.add(dateStr);
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let streak = 0;
    const currentDate = new Date(today);

    const todayStr = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;
    if (completedDates.has(todayStr)) {
      streak = 1;
      currentDate.setDate(currentDate.getDate() - 1);
    } else {
      currentDate.setDate(currentDate.getDate() - 1);
      const yesterdayStr = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;
      if (!completedDates.has(yesterdayStr)) {
        return 0;
      }
    }

    while (true) {
      const dateStr = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;
      if (completedDates.has(dateStr)) {
        streak++;
        currentDate.setDate(currentDate.getDate() - 1);
      } else {
        break;
      }
    }

    return streak;
  }, [sessions]);

  const upcomingAssignments = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const filtered = assignments.filter((a) => {
      if (!DASHBOARD_VISIBLE_ASSIGNMENT_STATUSES.includes(a.status)) {
        console.log('[ClientDashboard] Filtered out (status):', a.id, a.status);
        return false;
      }
      const scheduled = (a.scheduledFor as Timestamp).toDate();
      scheduled.setHours(0, 0, 0, 0);
      const isUpcoming = scheduled.getTime() >= today.getTime();
      if (!isUpcoming) {
        console.log('[ClientDashboard] Filtered out (date):', a.id, scheduled, 'vs', today);
      }
      return isUpcoming;
    });

    console.log('[ClientDashboard] Upcoming assignments:', filtered.length, filtered);
    return filtered;
  }, [assignments]);

  const handleSaveAvailability = async (slots: TimeSlot[]) => {
    if (!user) return;

    try {
      await updateDoc(doc(db, 'users', user.uid), {
        availabilitySlots: slots,
        updatedAt: Timestamp.now(),
      });

      const saveWeeklyScheduleFn = httpsCallable(functions, 'saveWeeklySchedule');
      const result = await saveWeeklyScheduleFn({});

      console.log('[ClientDashboard] Weekly schedule saved:', result.data);
      await refreshUserData();
    } catch (error) {
      console.error('[ClientDashboard] Error saving availability:', error);
      setError('Failed to save workout schedule. Please try again.');
      throw error;
    }
  };

  const handleReschedule = async (newDate: Date) => {
    if (!user || !rescheduleAssignment) return;

    try {
      const rescheduleWorkoutAssignmentFn = httpsCallable<
        { assignmentId: string; scheduledFor: string },
        { success: boolean; assignmentId: string; scheduledFor: string }
      >(functions, 'rescheduleWorkoutAssignment');

      await rescheduleWorkoutAssignmentFn({
        assignmentId: rescheduleAssignment.id,
        scheduledFor: newDate.toISOString(),
      });

      await scheduleWorkoutReminder(
        newDate,
        rescheduleAssignment.id,
        getAssignmentWorkoutName(rescheduleAssignment)
      );

      if (userData?.phone) {
        try {
          const sendSMSFn = httpsCallable(functions, 'sendSMSNotification');
          await sendSMSFn({
            to: userData.phone,
            message: `Your workout has been rescheduled to ${formatDateTimeForNotification(newDate)}. See you then! 💪`,
          });
          console.log('[ClientDashboard] Reschedule SMS sent');
        } catch (smsError) {
          console.warn('[ClientDashboard] SMS notification failed (non-fatal):', smsError);
        }
      }

      setShowRescheduleModal(false);
      setRescheduleAssignment(null);
    } catch (error) {
      console.error('[ClientDashboard] Error rescheduling:', error);
      throw error;
    }
  };

  const openRescheduleModal = (assignment: WorkoutAssignment) => {
    setRescheduleAssignment(assignment);
    setShowRescheduleModal(true);
  };

  const startDailySession = async () => {
    if (!user || !dailyTemplate) {
      setError('No daily workout available right now.');
      return;
    }

    try {
      const sessionRef = await startAdHocWorkoutSession({
        template: dailyTemplate,
        clientId: user.uid,
        trainerId: dailyTemplate.trainerId || trainerId || user.uid,
        sessionType: 'daily',
      });

      navigate(`/session/${sessionRef.id}`);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Unable to start daily workout');
    }
  };

  const startRotationSession = async (template: WorkoutTemplate) => {
    if (!user) {
      setError('Sign in to start your workout.');
      return;
    }

    setStartingId(template.id);
    setError(null);

    try {
      const sessionRef = await startAdHocWorkoutSession({
        template,
        clientId: user.uid,
        trainerId: template.trainerId || trainerId || user.uid,
        sessionType: 'ad_hoc',
      });

      navigate(`/session/${sessionRef.id}`);
    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Unable to start workout');
    } finally {
      setStartingId(null);
    }
  };

  const handlePremiumBookingAction = () => {
    if (shouldRequirePremium && !isPremiumActive) {
      setShowPaywall(true);
      return;
    }

    setShowBookingModal(true);
  };

  const dashboardError = error || assignmentsLoadError || sessionsLoadError;
  const coachLabel = trainerDetails.name ||
    (trainerId ? 'Your trainer' : (aiTrainerId ? 'AI Coach' : 'Not linked yet'));

  return (
    <>
      <SEOHead
        title="My Dashboard | Start Grounded"
        description="Your personal training dashboard — workouts, progress, and sessions"
        canonicalUrl="https://startgrounded.app/client"
      />
      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        onSave={handleSaveAvailability}
        initialSlots={userData?.availabilitySlots || []}
      />
      <Paywall
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        onPurchased={async () => {
          await refresh();
          setShowPaywall(false);
          setShowBookingModal(true);
        }}
      />
      <RescheduleModal
        isOpen={showRescheduleModal}
        onClose={() => {
          setShowRescheduleModal(false);
          setRescheduleAssignment(null);
        }}
        onSave={handleReschedule}
        currentDate={rescheduleAssignment ? (rescheduleAssignment.scheduledFor as Timestamp).toDate() : new Date()}
        workoutName={rescheduleAssignment ? getAssignmentWorkoutName(rescheduleAssignment, 'workout') : 'workout'}
      />
      <TrainerContactSheet
        isOpen={showContactSheet}
        onClose={() => setShowContactSheet(false)}
      />
      <div className="p-6" data-testid="dashboard">
        <div className="max-w-7xl mx-auto space-y-6">
          {showIntroPrompt && (
            <Card padding="lg" variant="elevated" className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    Ready to schedule your free intro session?
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-slate-400 mb-3">
                    Book your first workout with Coach Fit and get personalized AI-guided exercises based on your equipment and goals.
                  </p>
                </div>
                <Button
                  variant="primary"
                  onClick={() => navigate('/book-intro')}
                  data-testid="schedule-intro-button"
                >
                  Schedule Intro Session
                </Button>
              </div>
            </Card>
          )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-primary-600 dark:text-primary-400 uppercase tracking-wide font-semibold">
                Dashboard
              </p>
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
                {userData?.displayName || 'Client'}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  Coach: {coachLabel}
                </p>
                {(trainerId || aiTrainerId) && (
                  <button
                    onClick={() => setShowContactSheet(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-500/30 transition"
                    aria-label="Contact trainer"
                    data-testid="contact-trainer-btn"
                  >
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Contact
                  </button>
                )}
              </div>
            </div>
          </div>

          {trainerDetails.trainerProfile && (
            <TrainerCard trainer={trainerDetails.trainerProfile} />
          )}

          {dashboardError && (
            <div className={ALERTS.error}>{dashboardError}</div>
          )}

          {!isMobile && (
            <WeeklyProgress
              workoutStreak={workoutStreak}
              upcomingCount={upcomingAssignments.length}
              completedCount={sessions.filter(s => s.status === 'completed').length}
              totalSessions={sessions.length}
            />
          )}

          <WeeklyCalendar
            assignments={assignments}
            sessions={sessions}
            templateNames={templateNames}
            onStartWorkout={startSession}
          />

          <QuickActions
            dailyTemplate={dailyTemplate}
            dailyInProgressSession={dailyInProgressSession}
            rotationTemplates={rotationTemplates}
            rotationInProgressSessions={rotationInProgressSessions}
            inProgressSessions={sessions.filter(s => s.status === 'in_progress')}
            timezone={userData?.timezone}
            onStartDaily={startDailySession}
            onStartRotation={startRotationSession}
            onNavigateSession={(id) => navigate(`/session/${id}`)}
          />

          <div className="grid md:grid-cols-2 gap-6">
            <UpcomingWorkouts
              upcomingAssignments={upcomingAssignments}
              templateNames={templateNames}
              hasAvailabilitySlots={!!(userData?.availabilitySlots && userData.availabilitySlots.length > 0)}
              startingId={startingId}
              timezone={userData?.timezone}
              onBookSessions={handlePremiumBookingAction}
              onReschedule={openRescheduleModal}
              onStartSession={startSession}
            />

            <WorkoutHistory
              sessions={sessions}
              timezone={userData?.timezone}
              onSessionClick={(id) => navigate(`/session/${id}`)}
            />
          </div>
        </div>
      </div>
    </>
  );
}
